import React, { useEffect, useState, useCallback } from "react";
import Modal from "../../components/Modal";
import Icon from "../../components/Icon";
import { useChatContext } from "../../contexts/ChatContext";
import { getAuthToken } from "../../utils/authHelpers";
import { getCloudinaryUrl } from "../../utils/cloudinary";
import styles from "./ExploreNewCollabModal.module.sass";

/**
 * 2-step modal for the "Explore New Collab" flow.
 *
 * Step 1 – Search for a user or property (includes property results).
 * Step 2 – Confirm selected result, then navigate to /messages with collab-request params.
 */
const ExploreNewCollabModal = ({ visible, onClose, onExplore, onStartDraft }) => {
  const { searchUsers, isConnected, initializeChat } = useChatContext();

  const [step, setStep] = useState(1);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [chatInitializing, setChatInitializing] = useState(false);

  // Ensure chat is connected so searchUsers works
  useEffect(() => {
    if (!visible) return;
    if (isConnected) return;

    const token = getAuthToken();
    if (!token) return;

    setChatInitializing(true);
    initializeChat(token)
      .catch((err) => console.error("Failed to initialize chat for search:", err))
      .finally(() => setChatInitializing(false));
  }, [visible, isConnected, initializeChat]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setStep(1);
      setQuery("");
      setResults([]);
      setSelectedUser(null);
    }
  }, [visible]);

  // Debounced search (300ms) – includes properties for collab flow
  const performSearch = useCallback(
    async (q) => {
      if (!isConnected) return;
      setLoading(true);
      try {
        const res = await searchUsers(q, { includeProperties: true });
        setResults(res || []);
      } catch (err) {
        console.error("User search failed:", err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [isConnected, searchUsers]
  );

  useEffect(() => {
    if (!visible || step !== 1) return;
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, visible, step, performSearch]);

  // User selected → move to step 2
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setStep(2);
  };

  // Resolve the bare database userId from the search result object.
  const getRecipientUserId = (user) => {
    if (user.userId) return String(user.userId);
    if (user.id) {
      const parts = user.id.split("-");
      if (parts.length > 1 && ["creator", "host", "admin", "user", "traveler", "property"].includes(parts[0])) {
        return parts.slice(1).join("-");
      }
      return user.id;
    }
    return null;
  };

  // "Explore Collab" – delegate to parent handler
  const handleExplore = () => {
    if (!selectedUser) return;
    const recipientUserId = getRecipientUserId(selectedUser);
    if (!recipientUserId) return;
    onExplore(recipientUserId, selectedUser);
  };

  // "Start Draft" – create draft with selected recipient, navigate to editor
  const handleStartDraft = () => {
    if (!selectedUser) return;
    const recipientUserId = getRecipientUserId(selectedUser);
    if (!recipientUserId) return;
    onStartDraft?.(recipientUserId, selectedUser);
  };

  // "Create Blank Draft" – no recipient selected
  const handleBlankDraft = () => {
    onStartDraft?.(null, null);
  };

  const isProperty = (u) => u.resultType === "property";

  // Display helpers
  const userHandle = (u) => {
    const h = u.handle || u.username;
    return h ? `@${h.replace(/^@/, "")}` : null;
  };
  const userFirstName = (u) => u.first_name || (u.name ? u.name.split(" ")[0] : "");

  const propertyLocation = (u) => {
    const parts = [];
    if (u.locationCity) parts.push(u.locationCity);
    if (u.locationState) parts.push(u.locationState);
    return parts.join(", ");
  };

  const optimizedThumb = (url, isPropertyResult) => {
    if (!url || url.includes("ui-avatars.com")) return url;
    if (isPropertyResult) {
      return getCloudinaryUrl(url, { width: 88, height: 88, crop: "fill", quality: "auto", format: "auto" });
    }
    return getCloudinaryUrl(url, { width: 72, height: 72, crop: "fill", quality: "auto", format: "auto" });
  };

  const optimizedConfirm = (url, isPropertyResult) => {
    if (!url || url.includes("ui-avatars.com")) return url;
    if (isPropertyResult) {
      return getCloudinaryUrl(url, { width: 240, height: 160, crop: "fill", quality: "auto", format: "auto" });
    }
    return getCloudinaryUrl(url, { width: 144, height: 144, crop: "fill", quality: "auto", format: "auto" });
  };

  // Render a single result row
  const renderResultRow = (u) => {
    if (isProperty(u)) {
      const loc = propertyLocation(u);
      return (
        <button
          key={u.id}
          className={styles.userRow}
          onClick={() => handleSelectUser(u)}
        >
          <img
            src={optimizedThumb(u.image, true) || "/images/content/avatar-1.jpg"}
            alt={u.propertyName || "Property"}
            className={styles.propertyImg}
          />
          <div className={styles.meta}>
            <div className={styles.name}>
              <span style={{ color: "#0f084b" }}>
                {u.propertySlug ? `@${u.propertySlug}` : u.propertyName || "Property"}
              </span>
            </div>
            <div className={styles.propertyMeta}>
              <span className={styles.ownerName}>{u.ownerName}</span>
              {u.handle && (
                <span className={styles.ownerHandle}>@{u.handle.replace(/^@/, "")}</span>
              )}
            </div>
            {loc && <div className={styles.propertyLocation}>{loc}</div>}
          </div>
          <span className={styles.propertyBadge}>Property</span>
          <Icon name="arrow-right" size="16" />
        </button>
      );
    }

    const handle = userHandle(u);
    const firstName = userFirstName(u);
    return (
      <button
        key={u.id}
        className={styles.userRow}
        onClick={() => handleSelectUser(u)}
      >
        <img
          src={optimizedThumb(u.image, false) || "/images/content/avatar-1.jpg"}
          alt={u.name}
        />
        <div className={styles.meta}>
          <div className={styles.name}>
            {handle ? (
              <>
                <span style={{ color: "#0f084b" }}>{handle}</span>
                <span
                  style={{
                    marginLeft: "8px",
                    opacity: 0.7,
                    fontWeight: "400",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  {firstName}
                </span>
              </>
            ) : (
              u.name
            )}
          </div>
          {u.role && <div className={styles.role}>{u.role}</div>}
        </div>
        <Icon name="arrow-right" size="16" />
      </button>
    );
  };

  // Step 2 confirm display
  const confirmTitle = () => {
    if (isProperty(selectedUser)) {
      return selectedUser.propertySlug
        ? `@${selectedUser.propertySlug}`
        : selectedUser.propertyName || "Property";
    }
    return userHandle(selectedUser) || userFirstName(selectedUser) || selectedUser.name;
  };

  const confirmSubtitle = () => {
    if (isProperty(selectedUser)) {
      const parts = [];
      if (selectedUser.ownerName) parts.push(selectedUser.ownerName);
      if (selectedUser.handle) parts.push(`@${selectedUser.handle.replace(/^@/, "")}`);
      return parts.join(" · ") || null;
    }
    if (userHandle(selectedUser) && userFirstName(selectedUser)) {
      return userFirstName(selectedUser);
    }
    return null;
  };

  return (
    <Modal visible={visible} onClose={onClose} outerClassName={styles.outerWide}>
      <div className={styles.modal}>
        <h2 className={styles.title}>Explore New Collab</h2>
        <div className={styles.stepIndicator}>Step {step} of 2</div>

        {/* ── Step 1: Search ── */}
        {step === 1 && (
          <>
            {chatInitializing ? (
              <div className={styles.initLoading}>Connecting to chat...</div>
            ) : (
              <>
                <button
                  className={styles.blankDraftBtn}
                  onClick={handleBlankDraft}
                >
                  <Icon name="plus-circle" size="18" />
                  <span>Create Blank Draft</span>
                  <span className={styles.blankDraftHint}>No recipient yet</span>
                </button>
                <p className={styles.blankDraftTooltip}>
                  Start building your collab proposal without choosing a collaborator first. Outline expectations, deliverables, dates, and notes at your own pace. When you're ready, pick a creator or host to send it to. Nothing is shared until you hit "Send Proposal."
                </p>

                <div className={styles.searchRow}>
                  <div className={styles.searchBox}>
                    <Icon name="search" size="18" />
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search creators, hosts, and properties..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.results}>
                  {loading && <div className={styles.loading}>Searching...</div>}
                  {!loading && results.length === 0 && (
                    <div className={styles.empty}>No results found</div>
                  )}
                  {!loading && results.map((u) => renderResultRow(u))}
                </div>
              </>
            )}

            <div className={styles.actions}>
              <button className={styles.cancelBtn} onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Confirm ── */}
        {step === 2 && selectedUser && (
          <>
            <div className={styles.confirmSection}>
              {selectedUser.image ? (
                <img
                  src={optimizedConfirm(selectedUser.image, isProperty(selectedUser))}
                  alt={selectedUser.name}
                  className={isProperty(selectedUser) ? styles.confirmPropertyImg : styles.confirmAvatar}
                />
              ) : (
                <div className={styles.confirmAvatarPlaceholder}>
                  <Icon name="user" size="32" />
                </div>
              )}

              <div className={styles.confirmName}>{confirmTitle()}</div>

              {confirmSubtitle() && (
                <div className={styles.confirmHandle}>{confirmSubtitle()}</div>
              )}

              {selectedUser.role && (
                <span className={styles.confirmRole}>{selectedUser.role}</span>
              )}

              <p className={styles.confirmMessage}>
                Choose how to proceed with this collaboration.
              </p>
            </div>

            <div className={styles.actions}>
              <button className={styles.backBtn} onClick={() => setStep(1)}>
                Back
              </button>
              <button className={styles.draftBtn} onClick={handleStartDraft}>
                <Icon name="file" size="16" />
                Start Draft
              </button>
              <button className={styles.exploreBtn} onClick={handleExplore}>
                Send in Messages
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ExploreNewCollabModal;
